'use client';

import { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import { useAuth } from '@/platform/auth/useAuth';
import { fetchITSMMe, type ITSMMe } from './api';
import { peekOpsAuthReturn } from '@/platform/lib/idTokenStore';

const opsEnabled = process.env.NEXT_PUBLIC_COMMUNIO_OPS === 'true';

/** Ops home: domain reporters + agents → service desk; others see internal-only messaging. */
export function OpsHomeRedirect() {
  const router = useRouter();
  const { isAuthenticated, sessionReady } = useAuth();
  const [me, setMe] = useState<ITSMMe | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!opsEnabled || !sessionReady) return;
    if (!isAuthenticated) {
      setMe(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    void fetchITSMMe()
      .then(setMe)
      .catch(() => setMe(null))
      .finally(() => setLoading(false));
  }, [isAuthenticated, sessionReady]);

  useEffect(() => {
    if (!opsEnabled || loading) return;
    if (peekOpsAuthReturn()) return;
    if (me?.canAccess) {
      router.replace('/servicedesk/');
    }
  }, [me, loading, router]);

  if (!opsEnabled) return null;
  if (!sessionReady || loading) {
    return (
      <p className="text-muted" style={{ padding: '2rem' }}>
        Loading…
      </p>
    );
  }
  if (me?.canAccess) {
    return (
      <p className="text-muted" style={{ padding: '2rem' }}>
        Opening service desk…
      </p>
    );
  }
  return (
    <div style={{ padding: '2rem' }} data-testid="ops-internal-only">
      <h1>Communio ITSM</h1>
      <p className="text-muted">
        This is the Communio internal ITSM app (tickets, knowledge base, incidents, problems, and changes). Staff agents
        are assigned via the Workspace group; employees sign in with a{' '}
        <code>@{process.env.NEXT_PUBLIC_OPS_REPORTER_DOMAIN || 'communiosocial.com'}</code> account.
      </p>
      {!isAuthenticated && (
        <p className="text-muted">
          <a href="/login/?returnTo=/servicedesk/">Sign in</a> to open your tickets.
        </p>
      )}
      <p className="text-muted">
        To create or manage a community, use{' '}
        <a href="https://communiosocial.com/start/">communiosocial.com/start</a>.
      </p>
    </div>
  );
}
